import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { SegmentedTabs, TableCard, TableCardEmpty, TableCardLoading } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { Switch } from '@/components/ui';
import { Label } from '@/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { cn } from '@/components/ui';
import { toast } from 'sonner';
import { Badge } from '@/components/ui';
import {
  Tooltip,
  TooltipTrigger,
  DarkTooltipContent,
  TooltipProvider,
} from '@/components/ui';
import { Plus, Search, Edit, Trash2, Filter, Download, Upload, Copy, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useLLMConfigApi, type LLMConfigApi } from '../../services/llm-config-api';
import type { LLMConfig, ListLLMConfigsParams } from '../../types/llm-config';
import { LLMConfigDialog } from './LLMConfigDialog';
import { DeleteLLMDialog } from './DeleteLLMDialog';
import { LLMNodeOverview } from './LLMNodeOverview';
import { AppConfigProvider } from '../../context/AppConfigContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';

interface LLMManagementProps {
  app_id: string;
  className?: string;
  debug?: boolean;
  /** Optional API instance for testing */
  llmApi?: LLMConfigApi;
}

interface LLMManagementInnerProps {
  className?: string;
  llmApi?: LLMConfigApi;
}

type SortField = 'displayName' | 'provider' | 'outputTokenCost' | 'cacheReadTokenCost' | null;
type SortDirection = 'asc' | 'desc';

type LLMTab = 'models' | 'overview';

const LLM_TABS: { id: LLMTab; label: string }[] = [
  { id: 'models', label: 'Models' },
  { id: 'overview', label: 'Node Overview' },
];

const LLMManagementInner: React.FC<LLMManagementInnerProps> = ({
  className,
  llmApi: injectedApi,
}) => {
  const [activeTab, setActiveTab] = useState<LLMTab>('models');
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLLM, setSelectedLLM] = useState<LLMConfig | null>(null);
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mergeExisting, setMergeExisting] = useState(false);
  const [copyDefaultValues, setCopyDefaultValues] = useState<Partial<LLMConfig> | undefined>(undefined);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const defaultApi = useLLMConfigApi();
  const llmApi = injectedApi ?? defaultApi;

  // Build query params based on filters
  const queryParams: ListLLMConfigsParams = {};
  if (providerFilter !== 'all') {
    queryParams.provider = providerFilter;
  }
  if (statusFilter === 'active') {
    queryParams.activeOnly = true;
  } else if (statusFilter === 'inactive') {
    queryParams.activeOnly = false;
  }

  // Fetch LLM configs
  const { data: llmConfigs, isLoading } = useQuery<LLMConfig[]>({
    queryKey: ['llm-configs', queryParams],
    queryFn: () => llmApi.listLLMConfigs(queryParams),
    staleTime: 5 * 60_000,
  });

  // Create LLM mutation
  const createLLMMutation = useMutation({
    mutationFn: llmApi.createLLMConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-configs'] });
      toast.success('LLM Configuration created');
      setCreateDialogOpen(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create LLM configuration';
      toast.error('Error', { description: errorMessage });
    },
  });

  // Update LLM mutation
  const updateLLMMutation = useMutation({
    mutationFn: ({ modelName, data }: { modelName: string; data: any }) =>
      llmApi.updateLLMConfig(modelName, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-configs'] });
      toast.success('LLM Configuration updated');
      setEditDialogOpen(false);
      setSelectedLLM(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update LLM configuration';
      toast.error('Error', { description: errorMessage });
    },
  });

  // Delete LLM mutation
  const deleteLLMMutation = useMutation({
    mutationFn: llmApi.deleteLLMConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-configs'] });
      toast.success('LLM Configuration deleted');
      setDeleteDialogOpen(false);
      setSelectedLLM(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete LLM configuration';
      toast.error('Error', { description: errorMessage });
    },
  });

  // Export to CSV mutation
  const exportMutation = useMutation({
    mutationFn: llmApi.exportToCSV,
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `llm_configs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export successful', { description: 'LLM configurations exported to CSV.' });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export LLM configurations';
      toast.error('Error', { description: errorMessage });
    },
  });

  // Import from CSV mutation
  const importMutation = useMutation({
    mutationFn: ({ file, merge }: { file: File; merge: boolean }) =>
      llmApi.importFromCSV(file, merge),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['llm-configs'] });
      toast.success('Import successful', { description: result.message });
      setImportDialogOpen(false);
      setSelectedFile(null);
      setMergeExisting(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to import LLM configurations';
      toast.error('Error', { description: errorMessage });
    },
  });

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Invalid file', { description: 'Please select a CSV file.' });
        return;
      }
      setSelectedFile(file);
      setImportDialogOpen(true);
    }
  };

  // Handle import
  const handleImport = () => {
    if (selectedFile) {
      importMutation.mutate({ file: selectedFile, merge: mergeExisting });
    }
  };

  // Handle copy
  const handleCopy = (llm: LLMConfig) => {
    const { createdAt: _ca, updatedAt: _ua, createdBy: _cb, updatedBy: _ub, ...rest } = llm;
    const defaultValues = {
      ...rest,
      modelName: '',
      displayName: `${llm.displayName} (Copy)`,
    };
    setCopyDefaultValues(defaultValues);
    setCreateDialogOpen(true);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-1 h-3 w-3" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  // Filter and sort LLMs
  const filteredLLMs = React.useMemo(() => {
    if (!llmConfigs) return [];

    let result = llmConfigs;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (llm) =>
          llm.modelName.toLowerCase().includes(query) ||
          llm.modelId.toLowerCase().includes(query) ||
          llm.displayName.toLowerCase().includes(query) ||
          llm.provider.toLowerCase().includes(query)
      );
    }

    if (sortField) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        let cmp: number;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          cmp = aVal.localeCompare(bVal);
        } else {
          cmp = ((aVal as number) ?? 0) - ((bVal as number) ?? 0);
        }
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [llmConfigs, searchQuery, sortField, sortDirection]);

  // Get unique providers for filter dropdown
  const providers = React.useMemo(() => {
    if (!llmConfigs) return [];
    return Array.from(new Set(llmConfigs.map(llm => llm.provider)));
  }, [llmConfigs]);

  return (
    <div className={cn('llm-management text-slate-900', className)}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1
            className="text-[21px] font-[650] tracking-[-0.015em]"
            data-testid="llm-config-page-heading"
          >
            LLM Configuration
          </h1>
          <p className="mt-[5px] text-[13px] text-slate-500">
            The model catalog for analysis runs — models added here become selectable in
            pipelines, and their token prices drive the Costs dashboards.
          </p>
        </div>
        <SegmentedTabs tabs={LLM_TABS} value={activeTab} onChange={setActiveTab} />
      </div>

      {activeTab === 'overview' ? (
        <div className="mt-6">
          <LLMNodeOverview />
        </div>
      ) : (
        <>
          <div className="mt-6 mb-4 flex flex-wrap items-center gap-2.5">
            <div className="flex w-full max-w-xs items-center gap-2 rounded-[9px] border border-slate-200 bg-white px-3.5 py-[7px] text-[12.5px] shadow-[0_1px_2px_rgba(2,8,23,0.04)] focus-within:ring-2 focus-within:ring-ring">
              <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <input
                type="search"
                placeholder="Search by model ID, name, or provider..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent outline-none placeholder:text-slate-400"
              />
            </div>
            <Select value={providerFilter} onValueChange={setProviderFilter}>
              <SelectTrigger className="w-[180px] bg-white" aria-label="Filter by provider">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="All Providers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {providers.map(provider => (
                  <SelectItem key={provider} value={provider}>
                    {provider}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] bg-white" aria-label="Filter by status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <span className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
            >
              <Download className="mr-2 h-4 w-4" />
              {exportMutation.isPending ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button size="sm" onClick={() => {
              setCopyDefaultValues(undefined);
              setCreateDialogOpen(true);
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Add LLM Model
            </Button>
          </div>

          <TableCard
            title="Models"
            meta={
              filteredLLMs.length > 0
                ? `${filteredLLMs.length} model${filteredLLMs.length !== 1 ? 's' : ''} found`
                : undefined
            }
          >
            {isLoading ? (
              <TableCardLoading label="Loading LLM configurations…" />
            ) : filteredLLMs.length === 0 ? (
              <TableCardEmpty
                title="No LLM configurations found"
                description={
                  searchQuery || providerFilter !== 'all' || statusFilter !== 'all'
                    ? 'No models match your search or filters.'
                    : 'Add a model to make it available to pipelines and enable cost tracking for its usage.'
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('displayName')}
                    >
                      <span className="flex items-center">
                        Display Name
                        <SortIcon field="displayName" />
                      </span>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('provider')}
                    >
                      <span className="flex items-center">
                        Provider
                        <SortIcon field="provider" />
                      </span>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('outputTokenCost')}
                    >
                      <span className="flex items-center">
                        Output Cost
                        <SortIcon field="outputTokenCost" />
                      </span>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('cacheReadTokenCost')}
                    >
                      <span className="flex items-center">
                        Cache Read Cost
                        <SortIcon field="cacheReadTokenCost" />
                      </span>
                    </TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[140px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLLMs.map((llm) => (
                    <TableRow key={llm.modelName}>
                      <TableCell className="font-[550]">
                        {llm.displayName}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{llm.provider}</Badge>
                      </TableCell>
                      <TableCell>
                        {llm.isActive ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-[3px] text-[10.5px] font-[650] text-emerald-700">
                            Active
                          </span>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2 py-[3px] text-[10.5px] font-[650] text-slate-500">
                            Inactive
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-slate-500">
                        {llm.outputTokenCost != null ? `$${llm.outputTokenCost}` : '—'}
                      </TableCell>
                      <TableCell className="font-mono text-slate-500">
                        {llm.cacheReadTokenCost != null ? `$${llm.cacheReadTokenCost}` : '—'}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(llm.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <TooltipProvider delayDuration={300}>
                          <div className="flex items-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCopy(llm)}
                                >
                                  <Copy className="h-4 w-4" />
                                  <span className="sr-only">Copy model</span>
                                </Button>
                              </TooltipTrigger>
                              <DarkTooltipContent>Copy model</DarkTooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLLM(llm);
                                    setEditDialogOpen(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                  <span className="sr-only">Edit model</span>
                                </Button>
                              </TooltipTrigger>
                              <DarkTooltipContent>Edit model</DarkTooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLLM(llm);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                  <span className="sr-only">Delete model</span>
                                </Button>
                              </TooltipTrigger>
                              <DarkTooltipContent>Delete model</DarkTooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableCard>
        </>
      )}

      {/* Dialogs */}
      <LLMConfigDialog
        mode="create"
        open={createDialogOpen}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) {
            setCopyDefaultValues(undefined);
          }
        }}
        onSave={(data) => {
          createLLMMutation.mutate(data);
        }}
        isPending={createLLMMutation.isPending}
        defaultValues={copyDefaultValues}
      />

      {selectedLLM && (
        <>
          <LLMConfigDialog
            mode="edit"
            llm={selectedLLM}
            open={editDialogOpen}
            onOpenChange={(open) => {
              setEditDialogOpen(open);
              if (!open) setSelectedLLM(null);
            }}
            onSave={(data) => {
              updateLLMMutation.mutate({ modelName: selectedLLM.modelName, data });
            }}
            isPending={updateLLMMutation.isPending}
          />

          <DeleteLLMDialog
            llm={selectedLLM}
            open={deleteDialogOpen}
            onOpenChange={(open) => {
              setDeleteDialogOpen(open);
              if (!open) setSelectedLLM(null);
            }}
            onDelete={() => {
              deleteLLMMutation.mutate(selectedLLM.modelName);
            }}
            isPending={deleteLLMMutation.isPending}
          />
        </>
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import LLM Configurations</DialogTitle>
            <DialogDescription>
              Import LLM configurations from a CSV file.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedFile && (
              <div className="rounded-md border p-3">
                <p className="text-sm font-medium">Selected file:</p>
                <p className="text-sm text-muted-foreground">{selectedFile.name}</p>
              </div>
            )}

            <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="merge_existing" className="text-base">
                  Merge Existing
                </Label>
                <p className="text-sm text-muted-foreground">
                  Update existing configurations. If disabled, existing records will be skipped.
                </p>
              </div>
              <Switch
                id="merge_existing"
                checked={mergeExisting}
                onCheckedChange={setMergeExisting}
                disabled={importMutation.isPending}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false);
                setSelectedFile(null);
                setMergeExisting(false);
              }}
              disabled={importMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!selectedFile || importMutation.isPending}
            >
              {importMutation.isPending ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const LLMManagement: React.FC<LLMManagementProps> = ({
  app_id,
  className,
  debug = false,
  llmApi,
}) => {
  return (
    <AppConfigProvider app_id={app_id} debug={debug}>
      <LLMManagementInner className={className} llmApi={llmApi} />
    </AppConfigProvider>
  );
};
